import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { EnvironmentService } from '@core';
import { UploadConfig, UploadFile } from '@ntv360/component-pantry';

@Injectable({ providedIn: 'root' })
export class FilestackUploadService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly env = inject(EnvironmentService);

    /**
     * Uploads a single file to Filestack and returns both CDN and AWS S3 URLs.
     *
     * @param file - The file to upload. Can be a File, Blob, or string (URL/base64)
     * @param filename - Optional custom filename to use for the uploaded file
     * @returns Promise resolving to an object containing:
     *  - `cdnUrl`: The Filestack CDN URL of the uploaded file
     *  - `s3Url`: The direct AWS S3 URL of the uploaded file
     * @throws Error if Filestack API key, S3 bucket, or region is missing from environment config
     */
    async uploadFile(
        file: File | Blob | string,
        filename?: string,
    ): Promise<{ cdnUrl: string; s3Url: string }> {
        const apiKey = this.env.config.filestack.apiKey;
        const bucket = this.env.config.s3.bucketName;
        const region = this.env.config.s3.region;

        if (!apiKey || !bucket || !region) {
            throw new Error('Filestack or S3 configuration is missing');
        }

        const filestack = await import('filestack-js');
        const client = filestack.init(apiKey);

        const result = await client.upload(
            file,
            {},
            {
                filename,
                container: bucket,
                region: region,
            },
        );

        const cdnUrl = result.url;
        const s3Url = `https://${bucket}.s3.${region}.amazonaws.com/${result.key}`;

        return { cdnUrl, s3Url };
    }

    /**
     * Uploads multiple files to Filestack with progress tracking and optional video conversion.
     *
     * For video files with `optimizeVideo` enabled, triggers an async WebM conversion
     * via Filestack and returns a UUID for the backend to track the conversion webhook.
     *
     * @param files - Array of files to upload, each containing file metadata and the raw File object
     * @param config - Upload configuration including optional Filestack API key override
     * @param onProgress - Callback fired during upload with file ID and percent complete (0-100)
     * @param onFileComplete - Callback fired when a file finishes uploading, receives file ID, CDN URL, S3 URL, and optional conversion UUID
     * @param onFileError - Callback fired when a file upload fails, receives file ID and error message
     * @param onCancelRegistration - Optional callback that receives a cancel function to abort all in-progress uploads
     * @returns Promise that resolves when all files have been processed
     * @throws Error if S3 credentials or Filestack API key are missing
     */
    async uploadFiles(
        files: UploadFile[],
        config: UploadConfig,
        onProgress: (fileId: string, percent: number) => void,
        onFileComplete: (
            fileId: string,
            url: string,
            s3Url?: string,
            uuid?: string,
            optimizeVideo?: boolean,
        ) => void,
        onFileError: (fileId: string, error: string) => void,
        onCancelRegistration?: (cancelCallback: () => void) => void,
    ): Promise<void> {
        const apiKey = config.filestack?.apiKey || this.env.config.filestack.apiKey;
        const bucket = this.env.config.s3.bucketName;
        const region = this.env.config.s3.region;

        if (!bucket || !region) throw new Error('S3 credentials are required');
        if (!apiKey) throw new Error('Filestack apiKey is required');

        const filestack = await import('filestack-js');
        const client = filestack.init(apiKey);

        const tokens: any[] = [];
        if (onCancelRegistration) {
            onCancelRegistration(() => {
                tokens.forEach((t) => t.cancel?.());
            });
        }

        await Promise.all(
            files.map(async (f) => {
                try {
                    const token: any = {};
                    tokens.push(token);

                    const result = await client.upload(
                        f.file,
                        {
                            onProgress: ({ totalPercent }: { totalPercent: number }) => {
                                onProgress(f.id, totalPercent);
                            },
                        },
                        {
                            filename: f.extension
                                ? `${f.currentName}.${f.extension}`
                                : f.currentName,
                            container: bucket,
                            region: region,
                        },
                        token,
                    );

                    // Ensure 100% progress is emitted
                    onProgress(f.id, 100);

                    // Default to original upload URL
                    let url = result.url;
                    let uuid: string | undefined;

                    const isVideo = f.mimeType.startsWith('video/');

                    const videoConvertFormat = f.optimizeVideo ? 'webm' : f.extension;

                    if (isVideo && result.handle && isPlatformBrowser(this.platformId)) {
                        try {
                            const handle = result.handle;
                            const filename = result.key.substring(0, result.key.lastIndexOf('.'));

                            // Conversion URL with new filename to ensure S3 path consistency
                            const conversionUrl = `https://cdn.filestackcontent.com/${apiKey}/video_convert=preset:${videoConvertFormat},width:848,height:480,video_bitrate:1000,filename:${filename}/${handle}`;

                            const response = await fetch(conversionUrl);
                            const convResult = await response.json();

                            if (response.ok && convResult.uuid) {
                                // Save UUID so backend can match it when webhook fires
                                uuid = convResult.uuid;

                                // Keep original URL for now
                                // Backend webhook will update url to .webm S3 URL once done
                                url = result.url;
                            } else {
                                url = result.url;
                            }
                        } catch (error) {
                            console.error(
                                '[FilestackUploadService] Video conversion error:',
                                error,
                            );
                            // Fallback to original URL on error
                            url = result.url;
                        }
                    }

                    const s3Url = `https://${bucket}.s3.${region}.amazonaws.com/${result.key}`;

                    // Pass uuid to backend so it can match the webhook response later
                    onFileComplete(f.id, url, s3Url, uuid, f.optimizeVideo);
                } catch (err: unknown) {
                    onFileError(f.id, err instanceof Error ? err.message : 'Upload failed');
                    throw err;
                }
            }),
        );
    }
}
